import Link from "components/elements/link"
import PropTypes from "prop-types"
import React, { useRef, useState } from "react"
import { getOptimizedImage } from "utils/optimizeContentfulImage"
import {
  AbaIcon,
  ArrowLeft,
  ArrowRight,
  ArrowUpRight,
  ChevronDown,
  EvaluationsIcon,
  ExpansionIcon,
  FamilyIcon,
  HeadphonesIcon,
  LeadershipIcon,
  MentalHealthIcon,
  MicrophoneIcon,
  NewsIcon,
  OccupationalIcon,
  PartnershipsIcon,
  PresentationIcon,
  ShieldIcon,
  SpeechIcon,
  ViewAllIcon,
} from "./icons"
import "./press.scss"

const PAGE_SIZE = 6
const VIEW_ALL = "View All"

// Each Press Article category gets a tab with its own icon and color, in this
// order. A category added in Contentful but not listed here still gets a tab,
// after these, with the generic icon.
const CATEGORY_STYLES = {
  [VIEW_ALL]: { Icon: ViewAllIcon, modifier: "all" },
  Leadership: { Icon: LeadershipIcon, modifier: "yellow" },
  Expansion: { Icon: ExpansionIcon, modifier: "pink" },
  Partnerships: { Icon: PartnershipsIcon, modifier: "green" },
}
const DEFAULT_CATEGORY_STYLE = { Icon: NewsIcon, modifier: "blue" }

const MEDIA_EMAIL = "info@corticacare.com"

// Stand-in photos, from assets already on the live site, until the photos in
// the Figma are added.
const ABOUT_IMAGE =
  "https://images.ctfassets.net/67wivm6jm6qo/6vdo3qjGqyf9nmNXyXtf3f/32d6da20d5756013cf365e257953839e/helping-a-child.jpg"
const MEDIA_IMAGE =
  "https://images.ctfassets.net/67wivm6jm6qo/3RHK09RQUCtBooxAN4xRr4/95823789aa9f0f242324b2d1ff8b0d3b/Dr._Suzanne_Goh.jpg"

const SERVICE_CHIPS = [
  { Icon: EvaluationsIcon, label: "Evaluations", modifier: "green" },
  { Icon: AbaIcon, label: "ABA", modifier: "blue" },
  { Icon: SpeechIcon, label: "Speech", modifier: "pink" },
  { Icon: OccupationalIcon, label: "Occupational", modifier: "yellow" },
  { Icon: MentalHealthIcon, label: "Mental Health", modifier: "teal" },
]

const MEDIA_TOPICS = [
  { Icon: MicrophoneIcon, text: "Media interviews and expert quotes" },
  { Icon: HeadphonesIcon, text: "Podcasts and speaking engagements" },
  {
    Icon: PresentationIcon,
    text: "Presentations for healthcare providers, schools, and organizations",
  },
  {
    Icon: ShieldIcon,
    text: "Expert perspectives on autism therapy, early intervention, and neurodevelopmental care",
  },
  {
    Icon: FamilyIcon,
    text: "Integrated and family-centered approaches to autism care",
  },
]

const imageUrl = (article, width) =>
  article.image?.file?.url &&
  getOptimizedImage(`https:${article.image.file.url}`, width)

// Page numbers to show: the first and last three, plus the pages around the
// current one, with a gap marker wherever numbers are skipped. A gap of a
// single page shows that page instead, since the marker would take its place.
const pageNumbers = (current, count) => {
  const pages = new Set([1, 2, 3, count - 2, count - 1, count])
  ;[current - 1, current, current + 1].forEach(p => pages.add(p))
  const sorted = [...pages]
    .filter(p => p >= 1 && p <= count)
    .sort((a, b) => a - b)

  return sorted.reduce((list, page, i) => {
    const skipped = i > 0 ? page - sorted[i - 1] - 1 : 0
    if (skipped === 1) list.push(page - 1)
    if (skipped > 1) list.push(`gap-${page}`)
    list.push(page)
    return list
  }, [])
}

const FeaturedArticle = ({ article }) => (
  <article className="Press__featured">
    <div className="Press__featured-image">
      {article.image?.file?.url && (
        <img
          alt={article.image.description || ""}
          src={imageUrl(article, "contentMedium")}
        />
      )}
    </div>
    <div className="Press__featured-body">
      <p className="Press__eyebrow">{article.publication}</p>
      <h2 className="Press__featured-title">{article.title}</h2>
      <p className="Press__featured-summary">{article.summary?.summary}</p>
      <Link className="Press__read-more" to={article.articleLink}>
        Read More
        <span className="sr-only">
          {" "}
          about {article.title} (opens in a new tab)
        </span>
        <ArrowRight size={16} />
      </Link>
    </div>
  </article>
)

const ArticleCard = ({ article }) => (
  <li className="Press__card">
    <Link className="Press__card-link" to={article.articleLink}>
      <div className="Press__card-image">
        {article.image?.file?.url && (
          <img
            alt={article.image.description || ""}
            loading="lazy"
            src={imageUrl(article, "blogCard")}
          />
        )}
      </div>
      <div className="Press__card-body">
        <p className="Press__eyebrow">{article.publication}</p>
        <h3 className="Press__card-title">
          <span>{article.title}</span>
          <ArrowUpRight size={16} />
        </h3>
        <p className="Press__card-summary">{article.summary?.summary}</p>
        <span className="sr-only">(opens in a new tab)</span>
      </div>
    </Link>
  </li>
)

const Pagination = ({ count, current, onChange }) => (
  <nav aria-label="Press articles pages" className="Press__pagination">
    <button
      className="Press__page-step"
      disabled={current === 1}
      onClick={() => onChange(current - 1)}
      type="button"
    >
      <ArrowLeft size={16} />
      <span className="Press__page-step-label">Previous</span>
    </button>

    <ol className="Press__page-numbers">
      {pageNumbers(current, count).map(page =>
        typeof page === "number" ? (
          <li key={page}>
            <button
              aria-current={page === current ? "page" : undefined}
              className="Press__page-number"
              onClick={() => onChange(page)}
              type="button"
            >
              <span className="sr-only">Page </span>
              {page}
            </button>
          </li>
        ) : (
          <li aria-hidden="true" className="Press__page-gap" key={page}>
            &hellip;
          </li>
        )
      )}
    </ol>
    <p className="Press__page-status">
      Page {current} of {count}
    </p>

    <button
      className="Press__page-step"
      disabled={current === count}
      onClick={() => onChange(current + 1)}
      type="button"
    >
      <span className="Press__page-step-label">Next</span>
      <ArrowRight size={16} />
    </button>
  </nav>
)

const PressPage = ({ articles }) => {
  const [activeCategory, setActiveCategory] = useState(VIEW_ALL)
  const [page, setPage] = useState(1)
  const listRef = useRef(null)

  const featured = articles.find(article => article.featured) || articles[0]
  const others = articles.filter(article => article !== featured)
  const knownOrder = Object.keys(CATEGORY_STYLES)
  const rank = category =>
    knownOrder.includes(category)
      ? knownOrder.indexOf(category)
      : knownOrder.length
  const categories = [
    ...new Set(others.map(article => article.category).filter(Boolean)),
  ].sort((a, b) => rank(a) - rank(b))
  const tabs = [VIEW_ALL, ...categories]
  const filtered =
    activeCategory === VIEW_ALL
      ? others
      : others.filter(article => article.category === activeCategory)
  const pageCount = Math.ceil(filtered.length / PAGE_SIZE)
  const visible = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  const chooseCategory = category => {
    setActiveCategory(category)
    setPage(1)
  }

  const changePage = next => {
    setPage(next)
    listRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
  }

  return (
    <div className="Press">
      <section className="Press__hero">
        <div className="container-v2">
          <h1 className="Press__title">
            Cortica in the <u>News</u>
          </h1>
          <p className="Press__intro">
            The latest coverage of our whole-child approach to autism therapy
            and neurodevelopmental care.
          </p>
          <FeaturedArticle article={featured} />
        </div>
      </section>

      {others.length > 0 && (
        <section
          aria-label="More press coverage"
          className="Press__list"
          ref={listRef}
        >
          <div className="container-v2">
            {categories.length > 1 && (
              <>
                <div
                  aria-label="Filter by category"
                  className="Press__tabs"
                  role="group"
                >
                  {tabs.map(category => {
                    const { Icon, modifier } =
                      CATEGORY_STYLES[category] || DEFAULT_CATEGORY_STYLE
                    return (
                      <button
                        aria-pressed={category === activeCategory}
                        className="Press__tab"
                        key={category}
                        onClick={() => chooseCategory(category)}
                        type="button"
                      >
                        <span
                          className={`Press__tab-icon Press__tab-icon--${modifier}`}
                        >
                          <Icon size={20} />
                        </span>
                        {category}
                      </button>
                    )
                  })}
                </div>

                <label className="Press__select">
                  <span className="sr-only">Filter by category</span>
                  <select
                    onChange={e => chooseCategory(e.target.value)}
                    value={activeCategory}
                  >
                    {tabs.map(category => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </select>
                  <ChevronDown size={16} />
                </label>
              </>
            )}

            <ul className="Press__grid">
              {visible.map(article => (
                <ArticleCard article={article} key={article.id} />
              ))}
            </ul>

            {pageCount > 1 && (
              <Pagination
                count={pageCount}
                current={page}
                onChange={changePage}
              />
            )}
          </div>
        </section>
      )}

      <section className="Press__about">
        <div className="container-v2 Press__about-inner">
          <div className="Press__about-image">
            <img alt="" loading="lazy" src={`${ABOUT_IMAGE}?w=800&q=80`} />
            <ul aria-hidden="true" className="Press__chips">
              {SERVICE_CHIPS.map(({ Icon, label, modifier }) => (
                <li
                  className={`Press__chip Press__chip--${modifier}`}
                  key={label}
                >
                  <Icon size={20} />
                  {label}
                </li>
              ))}
            </ul>
          </div>
          <div className="Press__about-body">
            <h2 className="Press__section-title">About Cortica</h2>
            <p>
              Cortica provides autism therapy and neurodevelopmental care for
              children and adolescents through an integrated, whole-child model.
              Our team delivers coordinated services in one place, including
              evaluations, ABA, speech, occupational, and mental health care.
            </p>
            <p className="Press__strong">
              Learn more about the whole-child model
            </p>
            <Link
              className="Press__button Press__button--outline"
              to="/whole-child"
            >
              Whole-Child Model
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      <section className="Press__media">
        <div className="container-v2">
          <div className="Press__media-card">
            <div className="Press__media-body">
              <h2 className="Press__section-title">Media Inquiries</h2>
              <p>
                For press inquiries, interviews, podcast appearances, speaking
                engagements, presentations, or expert commentary, please contact
                our team.
              </p>
              <p className="Press__strong">
                Cortica experts are available for:
              </p>
              <ul className="Press__topics">
                {MEDIA_TOPICS.map(({ Icon, text }) => (
                  <li key={text}>
                    <Icon size={18} />
                    {text}
                  </li>
                ))}
              </ul>
              <a className="Press__button" href={`mailto:${MEDIA_EMAIL}`}>
                Contact Our Team
                <ArrowRight size={16} />
              </a>
            </div>
            <div className="Press__media-image">
              <img
                alt="Dr. Suzanne Goh, Cortica co-founder and chief medical officer"
                loading="lazy"
                src={`${MEDIA_IMAGE}?w=800&q=80`}
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

const ArticleProps = PropTypes.shape({
  articleLink: PropTypes.string,
  category: PropTypes.string,
  datePublished: PropTypes.string,
  featured: PropTypes.bool,
  id: PropTypes.string,
  image: PropTypes.shape({
    description: PropTypes.string,
    file: PropTypes.shape({ url: PropTypes.string }),
    title: PropTypes.string,
  }),
  publication: PropTypes.string,
  summary: PropTypes.shape({ summary: PropTypes.string }),
  title: PropTypes.string,
})

FeaturedArticle.propTypes = { article: ArticleProps }
ArticleCard.propTypes = { article: ArticleProps }
Pagination.propTypes = {
  count: PropTypes.number,
  current: PropTypes.number,
  onChange: PropTypes.func,
}
PressPage.propTypes = { articles: PropTypes.arrayOf(ArticleProps) }

export default PressPage
